/**
 * Types and parser for workflow run session report data.
 * API returns reports as an array of report objects; this module parses and types them.
 */

export type WorkflowRunReportSection = {
  id: string
  title: string
  content: string
}

export type ParsedWorkflowRunReport = {
  id: string
  title: string
  summary?: string
  generated_at?: string
  sections: WorkflowRunReportSection[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseSection(raw: unknown): WorkflowRunReportSection | null {
  if (!isRecord(raw)) return null
  const id = raw.id != null ? String(raw.id) : raw.section_id != null ? String(raw.section_id) : null
  const title = raw.title != null ? String(raw.title) : raw.name != null ? String(raw.name) : null
  const content = raw.content != null ? String(raw.content) : raw.body != null ? String(raw.body) : null
  if (id && title !== null) {
    return { id, title, content: content ?? '' }
  }
  return null
}

/**
 * Parse a single report object from the workflow run response.
 * Accepts common API shapes: { id, title, summary, generated_at, sections } or similar.
 */
export function parseWorkflowRunReport(raw: unknown): ParsedWorkflowRunReport | null {
  if (!isRecord(raw)) return null

  const id = raw.id != null ? String(raw.id) : raw.report_id != null ? String(raw.report_id) : ''
  const title =
    raw.title != null ? String(raw.title) : raw.name != null ? String(raw.name) : 'Report'
  const summary = raw.summary != null ? String(raw.summary) : undefined
  const generated_at =
    raw.generated_at != null ? String(raw.generated_at) : raw.created_at != null ? String(raw.created_at) : undefined

  let sections: WorkflowRunReportSection[] = []
  const rawSections = raw.sections ?? raw.report_sections
  if (Array.isArray(rawSections)) {
    for (const item of rawSections) {
      const section = parseSection(item)
      if (section) sections.push(section)
    }
  }

  return { id: id || 'report', title, summary, generated_at, sections }
}

/**
 * Parse the reports array from workflow run detail (runDetails.reports).
 */
export function parseWorkflowRunReports(raw: unknown): ParsedWorkflowRunReport[] {
  if (!Array.isArray(raw)) return []
  const out: ParsedWorkflowRunReport[] = []
  for (const item of raw) {
    const report = parseWorkflowRunReport(item)
    if (report) out.push(report)
  }
  return out
}

/** Sample report data for demo and tests (matches parsed structure) */
export const SAMPLE_WORKFLOW_RUN_REPORT: ParsedWorkflowRunReport = {
  id: 'sample-report-1',
  title: 'Product Yield Analysis Report',
  summary:
    'This report summarizes yield trends and excursion events for the selected product and lot date range.',
  generated_at: new Date().toISOString(),
  sections: [
    {
      id: 'executive-summary',
      title: 'Executive Summary',
      content:
        'Yield for the period remained stable at 94.2% with no critical excursions. Two minor deviations were observed in Lot A-101 and Lot B-204, both resolved within 24 hours.',
    },
    {
      id: 'yield-trend',
      title: 'Yield Trend Analysis',
      content:
        'The yield trend chart shows a slight improvement over the previous period. Key metrics:\n\n- Average yield: 94.2%\n- Best performing lot: C-301 (96.1%)\n- Lots below target: 2 of 24\n\nRecommendation: Continue current process parameters; monitor Lot A-101 for recurrence.',
    },
    {
      id: 'excursion-summary',
      title: 'Excursion Summary',
      content:
        '**Excursion events (2)**\n\n1. **Lot A-101** – Low yield on step 3 (etch). Root cause: temperature drift. Corrective action applied.\n\n2. **Lot B-204** – Defect cluster in zone 2. Investigation ongoing; lot on hold.',
    },
    {
      id: 'recommendations',
      title: 'Recommendations',
      content:
        '1. Schedule preventive maintenance for etcher module used in Lot A-101.\n2. Review zone 2 metrology data for Lot B-204 before release.\n3. Consider extending sampling for next 3 lots from same product group.',
    },
  ],
}

/** Raw form of sample report (as API would return) for mock and tests */
export const SAMPLE_WORKFLOW_RUN_REPORT_RAW: Record<string, unknown> = {
  id: SAMPLE_WORKFLOW_RUN_REPORT.id,
  title: SAMPLE_WORKFLOW_RUN_REPORT.title,
  summary: SAMPLE_WORKFLOW_RUN_REPORT.summary,
  generated_at: SAMPLE_WORKFLOW_RUN_REPORT.generated_at,
  sections: SAMPLE_WORKFLOW_RUN_REPORT.sections.map((s) => ({
    id: s.id,
    title: s.title,
    content: s.content,
  })),
}
